import React, { useState, useRef } from 'react';
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Lock as LockIcon,
  Person as PersonIcon
} from '@mui/icons-material';

/**
 * PaymentForm Component
 * Mock payment form for credit purchase
 * Phase 7: Credit System
 * 
 * Features:
 * - Card number validation (Luhn algorithm)
 * - Expiry date format (MM/YY)
 * - CVV validation (3-4 digits)
 * - Card holder name
 * - Loading state
 * - Error handling
 */
const PaymentForm = ({ onSubmit, loading = false, disabled = false }) => {
  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardHolder: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [show3DSecure, setShow3DSecure] = useState(false);
  const timeoutRef = useRef(null);

  /**
   * Get card type from card number
   */
  const getCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s+/g, '');
    if (/^4/.test(cleaned)) return 'visa';
    if (/^5[1-5]/.test(cleaned)) return 'mastercard';
    if (/^3[47]/.test(cleaned)) return 'amex';
    return 'default';
  };

  /**
   * Luhn algorithm for card number validation
   */
  const validateCardNumber = (cardNumber) => {
    // Remove spaces and non-digits
    const cleaned = cardNumber.replace(/\s+/g, '').replace(/\D/g, '');
    
    if (cleaned.length < 13 || cleaned.length > 19) {
      return false;
    }

    let sum = 0;
    let isEven = false;

    // Start from the rightmost digit
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i], 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) {
          digit -= 9;
        }
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  /**
   * Format card number with spaces (XXXX XXXX XXXX XXXX)
   */
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s+/g, '').replace(/\D/g, '');
    const formatted = cleaned.match(/.{1,4}/g)?.join(' ') || cleaned;
    return formatted;
  };

  /**
   * Validate expiry date (MM/YY format)
   */
  const validateExpiryDate = (expiryDate) => {
    if (!expiryDate) return false;
    
    const regex = /^(\d{2})\/(\d{2})$/;
    const match = expiryDate.match(regex);
    
    if (!match) return false;
    
    const month = parseInt(match[1], 10);
    const year = parseInt(match[2], 10);
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    
    if (month < 1 || month > 12) return false;
    if (year < currentYear) return false;
    if (year === currentYear && month < currentMonth) return false;
    
    return true;
  };

  /**
   * Format expiry date (MM/YY)
   */
  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  /**
   * Validate CVV (3-4 digits)
   */
  const validateCVV = (cvv) => {
    return /^\d{3,4}$/.test(cvv);
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    
    if (field === 'cardNumber') {
      const formatted = formatCardNumber(value);
      setFormData(prev => ({ ...prev, [field]: formatted }));
    } else if (field === 'expiryDate') {
      const formatted = formatExpiryDate(value);
      setFormData(prev => ({ ...prev, [field]: formatted }));
    } else if (field === 'cvv') {
      // Only allow digits, max 4
      const cleaned = value.replace(/\D/g, '').slice(0, 4);
      setFormData(prev => ({ ...prev, [field]: cleaned }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field);
  };

  const validateField = (field) => {
    const value = formData[field];
    let error = null;

    switch (field) {
      case 'cardNumber':
        if (!value) {
          error = 'Kart numarası gereklidir. Lütfen kartınızın ön yüzündeki 16 haneli numarayı giriniz.';
        } else if (!validateCardNumber(value)) {
          error = 'Geçersiz kart numarası. Lütfen geçerli bir kart numarası giriniz (13-19 haneli).';
        }
        break;
      case 'expiryDate':
        if (!value) {
          error = 'Son kullanma tarihi gereklidir. Lütfen kartınızın ön yüzündeki son kullanma tarihini giriniz.';
        } else if (!validateExpiryDate(value)) {
          error = 'Geçersiz son kullanma tarihi. Lütfen MM/YY formatında geçerli bir tarih giriniz (örn: 12/25).';
        }
        break;
      case 'cvv':
        if (!value) {
          error = 'CVV gereklidir. Lütfen kartınızın arka yüzündeki 3 veya 4 haneli güvenlik kodunu giriniz.';
        } else if (!validateCVV(value)) {
          error = 'Geçersiz CVV. Lütfen 3 veya 4 haneli güvenlik kodunu giriniz.';
        }
        break;
      case 'cardHolder':
        if (!value) {
          error = 'Kart sahibi adı gereklidir. Lütfen kart üzerindeki ismi tam olarak giriniz.';
        } else if (value.length < 3) {
          error = 'Kart sahibi adı en az 3 karakter olmalıdır. Lütfen kart üzerindeki ismi tam olarak giriniz.';
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateForm = () => {
    const fields = ['cardNumber', 'expiryDate', 'cvv', 'cardHolder'];
    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field)) {
        isValid = false;
      }
      setTouched(prev => ({ ...prev, [field]: true }));
    });

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // 3D Secure simülasyonu (mock mode için)
    setShow3DSecure(true);
    
    // 2 saniye bekle
    timeoutRef.current = setTimeout(() => {
      setShow3DSecure(false);
      
      // Prepare payment data
      const paymentData = {
        cardNumber: formData.cardNumber.replace(/\s+/g, ''),
        expiryDate: formData.expiryDate,
        cvv: formData.cvv,
        cardHolder: formData.cardHolder.trim()
      };

      onSubmit(paymentData);
    }, 2000);
  };

  const handleCancel3DSecure = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShow3DSecure(false);
  };

  const isFormValid = () => {
    return formData.cardNumber && 
           formData.expiryDate && 
           formData.cvv && 
           formData.cardHolder &&
           !errors.cardNumber &&
           !errors.expiryDate &&
           !errors.cvv &&
           !errors.cardHolder;
  };

  return (
    <>
      {/* 3D Secure Dialog */}
      <Dialog open={show3DSecure} disableEscapeKeyDown>
        <DialogTitle>3D Secure Doğrulama</DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" gutterBottom>
              3D Secure doğrulanıyor...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Doğrulama kodu: 1234
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancel3DSecure}
            color="inherit"
          >
            İptal
          </Button>
        </DialogActions>
      </Dialog>

      <form onSubmit={handleSubmit}>
      {/* Card Visual */}
      <Card 
        sx={{ 
          mb: 3, 
          bgcolor: 'primary.dark', 
          color: 'white', 
          p: 2,
          background: getCardType(formData.cardNumber) === 'visa' 
            ? 'linear-gradient(135deg, #1a1f71 0%, #192f6d 100%)'
            : getCardType(formData.cardNumber) === 'mastercard'
            ? 'linear-gradient(135deg, #eb001b 0%, #f79e1b 100%)'
            : getCardType(formData.cardNumber) === 'amex'
            ? 'linear-gradient(135deg, #006fcf 0%, #00a8e8 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: 3,
          boxShadow: 6,
          transition: 'all 0.3s ease',
          transform: 'perspective(1000px) rotateY(0deg)',
          '&:hover': {
            transform: 'perspective(1000px) rotateY(2deg)',
            boxShadow: 8
          }
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
              {getCardType(formData.cardNumber) === 'visa' ? 'VISA' : 
               getCardType(formData.cardNumber) === 'mastercard' ? 'MASTERCARD' :
               getCardType(formData.cardNumber) === 'amex' ? 'AMEX' : 'CARD'}
            </Typography>
            <CreditCardIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography 
            variant="h5" 
            sx={{ 
              my: 2, 
              fontFamily: 'monospace',
              letterSpacing: 2,
              fontSize: { xs: '1.2rem', sm: '1.5rem' }
            }}
          >
            {formData.cardNumber || 'XXXX XXXX XXXX XXXX'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                KART SAHİBİ
              </Typography>
              <Typography variant="body1" sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}>
                {formData.cardHolder || 'AD SOYAD'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                SON KULLANMA
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formData.expiryDate || 'MM/YY'}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Card Number */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Kart Numarası"
            value={formData.cardNumber}
            onChange={handleChange('cardNumber')}
            onBlur={handleBlur('cardNumber')}
            error={touched.cardNumber && !!errors.cardNumber}
            helperText={touched.cardNumber && errors.cardNumber}
            disabled={loading || disabled}
            placeholder="1234 5678 9012 3456"
            autoComplete="cc-number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CreditCardIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: 19 // 16 digits + 3 spaces
            }}
          />
        </Grid>

        {/* Expiry Date */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Son Kullanma Tarihi"
            value={formData.expiryDate}
            onChange={handleChange('expiryDate')}
            onBlur={handleBlur('expiryDate')}
            error={touched.expiryDate && !!errors.expiryDate}
            helperText={touched.expiryDate ? errors.expiryDate : 'MM/YY formatında'}
            disabled={loading || disabled}
            placeholder="12/25"
            autoComplete="cc-exp"
            inputProps={{
              maxLength: 5
            }}
          />
        </Grid>

        {/* CVV */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="CVV"
            value={formData.cvv}
            onChange={handleChange('cvv')}
            onBlur={handleBlur('cvv')}
            error={touched.cvv && !!errors.cvv}
            helperText={touched.cvv && errors.cvv}
            disabled={loading || disabled}
            placeholder="123"
            type="password"
            autoComplete="cc-csc"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: 4
            }}
          />
        </Grid>

        {/* Card Holder */}
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Kart Sahibi Adı"
            value={formData.cardHolder}
            onChange={handleChange('cardHolder')}
            onBlur={handleBlur('cardHolder')}
            error={touched.cardHolder && !!errors.cardHolder}
            helperText={touched.cardHolder && errors.cardHolder}
            disabled={loading || disabled}
            placeholder="AD SOYAD"
            autoComplete="cc-name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Submit Button */}
        <Grid size={{ xs: 12 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            disabled={!isFormValid() || loading || disabled}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'İşleniyor...' : 'Ödemeyi Tamamla'}
          </Button>
        </Grid>
      </Grid>
    </form>
    </>
  );
};

export default PaymentForm;

